// src/pages/GuestLoginPage.tsx
// Guest-only login page - Staff login is at /login-staff
import React, { useState, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    Alert,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormLabel,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    Grid,
    IconButton,
    useTheme,
    Tooltip,
} from '@mui/material';
import {
    Hotel as HotelIcon,
    Business as BusinessIcon,
    LightMode,
    DarkMode,
    KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { UserRole } from '../types/chat';
import { isGuestProfileIncomplete } from '../shared/validators';

// Flag country codes for languages
const languageFlags: Record<string, string> = {
    en: 'gb',
    tr: 'tr',
    de: 'de',
    fr: 'fr',
    ru: 'ru',
    ar: 'ae',
};

// Flag icon component
const FlagIcon: React.FC<{ countryCode: string; size?: number }> = ({ countryCode, size = 24 }) => {
    return (
        <Box
            component="img"
            src={`https://flagcdn.com/w${size}/${countryCode}.png`}
            alt=""
            sx={{
                width: size,
                height: size * 0.75,
                objectFit: 'cover',
                borderRadius: '2px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                border: '0.5px solid rgba(0, 0, 0, 0.1)',
            }}
            onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = `https://flagcdn.com/${countryCode}.svg`;
            }}
        />
    );
};

const HeyHotelLogo: React.FC<{ fontSize?: number }> = ({ fontSize = 26 }) => (
    <Box
        component="img"
        src="/heyhotelai-logo.png"
        alt="HeyHotel.AI"
        sx={{ height: fontSize, width: 'auto', objectFit: 'contain' }}
    />
);

interface Hotel {
    id: string;
    name: string;
    code: string;
}

interface Guest {
    profileId?: string;
    userId?: string;
    id?: string;
    firstName: string;
    lastName: string;
    guestProfile: any;
}

const GuestLoginPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const accentColor = '#189BF0';
    const accentSoft = '#4EB0F5';
    const accentHover = '#1586d0';
    const pageBg = isDark ? theme.palette.background.default : '#EEF2F7';
    const surfaceBg = isDark ? theme.palette.background.paper : '#FFFFFF';
    const surfaceBorder = isDark ? `1px solid ${theme.palette.divider}` : '1px solid #E4E9F1';
    const mutedBorderColor = isDark ? theme.palette.divider : '#E4E9F1';
    const cardBg = isDark ? theme.palette.background.paper : '#FFFFFF';
    const selectedCardBg = isDark ? 'rgba(24, 155, 240, 0.12)' : '#EAF6FE';
    const primaryTextColor = isDark ? '#f8fafc' : '#28324A';
    const secondaryTextColor = isDark ? 'rgba(248, 250, 252, 0.75)' : '#8A94A8';
    const mutedTextColor = isDark ? 'rgba(226, 232, 240, 0.85)' : '#3B455A';
    const chipBtnSx = {
        width: 46,
        height: 46,
        borderRadius: '14px',
        border: `1px solid ${mutedBorderColor}`,
        backgroundColor: isDark ? theme.palette.action.hover : '#fff',
        color: isDark ? '#f8fafc' : '#15294D',
        boxShadow: isDark ? 'none' : '0 1px 4px rgba(21,41,77,.06)',
        '&:hover': {
            backgroundColor: isDark ? theme.palette.action.selected : '#F4F7FB',
        },
    };

    const handleThemeToggle = () => {
        const newMode = isDark ? 'light' : 'dark';
        localStorage.setItem('themeMode', newMode);
        window.dispatchEvent(new CustomEvent('themeChange', { detail: newMode }));
    };

    // Guest login state
    const [surname, setSurname] = useState('');
    const [reservationId, setReservationId] = useState('');
    const [selectedHotel, setSelectedHotel] = useState('');
    const [hotels, setHotels] = useState<Hotel[]>([]);
    const [hotelsLoading, setHotelsLoading] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [surnameError, setSurnameError] = useState('');
    const [reservationIdError, setReservationIdError] = useState('');
    const [hotelError, setHotelError] = useState('');
    const [unknownReservationSending, setUnknownReservationSending] = useState(false);
    const [unknownReservationFeedback, setUnknownReservationFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Guest selection state
    const [guests, setGuests] = useState<Guest[]>([]);
    const [selectedProfileId, setSelectedProfileId] = useState('');
    const [selectedGuestIndex, setSelectedGuestIndex] = useState<number | null>(null);
    const [reservation, setReservation] = useState<any>(null);
    const [showGuestSelection, setShowGuestSelection] = useState(false);

    // Add guest form state
    const [showAddGuestDialog, setShowAddGuestDialog] = useState(false);
    const [addGuestForm, setAddGuestForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        birthdate: '',
        nationalId: '',
        nationality: '',
        country: '',
        city: '',
        postCode: '',
        sex: '',
        kvkk: false,
        passportId: '',
        productInterests: [] as string[],
        allergies: [] as string[],
    });

    const { login, selectGuest } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const hasProcessedGuestSelection = useRef(false);
    const hasAppliedHotelCode = useRef(false);

    // Deep-link pins the hotel: when the URL carries hotelCode (e.g. MAGIST) or
    // hotelId, the operator must not switch to a different hotel from the dropdown.
    const isHotelLockedFromUrl = useMemo(
        () => Boolean(searchParams.get('hotelCode') || searchParams.get('hotelId')),
        [searchParams],
    );

    const applyHotelFromDeepLink = React.useCallback(async (knownHotels: Hotel[] = []) => {
        const hotelCodeParam = searchParams.get('hotelCode');
        const hotelIdParam = searchParams.get('hotelId');

        if (hasAppliedHotelCode.current) {
            return;
        }

        if (hotelIdParam) {
            setSelectedHotel(hotelIdParam);
            hasAppliedHotelCode.current = true;
        }

        if (!hotelCodeParam) {
            return;
        }

        try {
            const response = await api.get(
                `/auth/deep-link/resolve?hotelCode=${encodeURIComponent(hotelCodeParam)}`,
            );
            const data = response.data;
            const resolvedHotel: Hotel = {
                id: data.hotelId,
                name: data.hotelName,
                code: data.hotelCode,
            };

            setHotels((prev) => {
                const exists = prev.some(
                    (h) =>
                        h.id === resolvedHotel.id ||
                        h.code.toLowerCase() === resolvedHotel.code.toLowerCase(),
                );
                if (exists) {
                    return prev;
                }
                return [...prev, resolvedHotel];
            });
            setSelectedHotel(data.hotelId);
            hasAppliedHotelCode.current = true;
        } catch (err) {
            console.error('Failed to resolve hotel from deep link:', err);
            const match = knownHotels.find(
                (h) => h.code.toLowerCase() === hotelCodeParam.toLowerCase(),
            );
            if (match) {
                setSelectedHotel(match.id);
                hasAppliedHotelCode.current = true;
            }
        }
    }, [searchParams]);

    // Fetch hotels on component mount
    React.useEffect(() => {
        fetchAllHotels();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Pre-select hotel when opened via HeyHotelLoginURL or ?hotelCode= / ?hotelId=
    React.useEffect(() => {
        if (hasAppliedHotelCode.current || hotelsLoading) {
            return;
        }
        const hotelCodeParam = searchParams.get('hotelCode');
        const hotelIdParam = searchParams.get('hotelId');
        if (!hotelCodeParam && !hotelIdParam) {
            return;
        }
        void applyHotelFromDeepLink(hotels);
    }, [hotelsLoading, hotels, searchParams, applyHotelFromDeepLink]);

    // Check if we should show guest selection when coming back from add-guest page
    React.useEffect(() => {
        const showGuestSelectionParam = searchParams.get('showGuestSelection');
        const hotelIdParam = searchParams.get('hotelId');
        const reservationIdParam = searchParams.get('reservationId');
        const surnameParam = searchParams.get('surname');

        if (showGuestSelectionParam === 'true' && hotelIdParam && reservationIdParam && !hasProcessedGuestSelection.current && !showGuestSelection) {
            hasProcessedGuestSelection.current = true;
            window.history.replaceState({}, '', window.location.pathname);

            setSelectedHotel(hotelIdParam);
            setReservationId(reservationIdParam);
            if (surnameParam) {
                setSurname(surnameParam);
            }

            login(hotelIdParam, surnameParam || surname || '', reservationIdParam)
                .then((result) => {
                    if (result.guests && result.guests.length > 0) {
                        const guestOnlyList = result.guests.filter((guest: any) =>
                            !guest.role || guest.role === UserRole.GUEST || guest.role === 'GUEST' || guest.role === 'guest'
                        );
                        setGuests(guestOnlyList);
                        setReservation(result.reservation);
                        setShowGuestSelection(true);
                        setSelectedGuestIndex(null);
                        setSelectedProfileId('');
                    }
                })
                .catch((error) => {
                    console.error('Failed to reload guests:', error);
                });
        }

        if (showGuestSelectionParam !== 'true') {
            hasProcessedGuestSelection.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchAllHotels = async () => {
        setHotelsLoading(true);
        try {
            const [localResponse, pmsResponse] = await Promise.allSettled([
                api.get('/hotels/public/list'),
                api.get('/gsc-api/hotels'),
            ]);

            const localHotels = localResponse.status === 'fulfilled' ? localResponse.value.data : [];
            const pmsHotels = pmsResponse.status === 'fulfilled' ? pmsResponse.value.data : [];

            const hotelMap = new Map<string, Hotel>();

            localHotels.forEach((hotel: Hotel) => {
                hotelMap.set(hotel.id, hotel);
            });

            pmsHotels.forEach((pmsHotel: any) => {
                const existingLocalHotel = localHotels.find((h: Hotel) =>
                    h.code === pmsHotel.code || h.id === pmsHotel.id
                );

                if (!existingLocalHotel) {
                    hotelMap.set(pmsHotel.id, {
                        id: pmsHotel.id,
                        name: pmsHotel.name,
                        code: pmsHotel.code,
                    });
                }
            });

            const combined = Array.from(hotelMap.values());
            setHotels(combined);

            const localFailed = localResponse.status === 'rejected';
            const pmsFailed = pmsResponse.status === 'rejected';
            if (combined.length === 0 && (localFailed || pmsFailed)) {
                setError(t('login.hotelsLoadFailed'));
            } else {
                setError('');
            }
        } catch (error) {
            console.error('Failed to fetch hotels:', error);
            setError(t('login.hotelsLoadFailed'));
            setHotels([]);
        } finally {
            setHotelsLoading(false);
        }
    };

    const validateSurname = (value: string): boolean => {
        if (!value || !value.trim()) {
            setSurnameError(t('login.surnameRequired'));
            return false;
        }
        setSurnameError('');
        return true;
    };

    const validateReservationId = (value: string): boolean => {
        if (!value || !value.trim()) {
            setReservationIdError(t('login.reservationIdRequired'));
            return false;
        }
        setReservationIdError('');
        return true;
    };

    const validateHotel = (hotelId: string): boolean => {
        // Allow empty hotel for superadmin login
        const isSuperadminLogin = reservationId === '1' && surname?.toUpperCase() === 'SUPERADMIN';

        if (!hotelId || !hotelId.trim()) {
            if (isSuperadminLogin) {
                setHotelError('');
                return true;
            }
            setHotelError(t('login.hotelRequiredError'));
            return false;
        }
        setHotelError('');
        return true;
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setShowGuestSelection(false);
        setUnknownReservationFeedback(null);

        const isSuperadminLogin = reservationId === '1' && surname?.toUpperCase() === 'SUPERADMIN';

        const isHotelValid = validateHotel(selectedHotel);
        const isSurnameValid = validateSurname(surname);
        const isReservationIdValid = validateReservationId(reservationId);

        if (!isHotelValid || !isSurnameValid || !isReservationIdValid) {
            return;
        }

        setIsLoading(true);

        try {
            const hotelIdForLogin = isSuperadminLogin ? 'none' : selectedHotel;
            const result = await login(hotelIdForLogin, surname, reservationId);

            if (result.guests && result.guests.length > 0) {
                const guestOnlyList = result.guests.filter((guest: any) =>
                    !guest.role || guest.role === UserRole.GUEST || guest.role === 'GUEST' || guest.role === 'guest'
                );
                setGuests(guestOnlyList);
                setReservation(result.reservation);
                setShowGuestSelection(true);
                setSelectedGuestIndex(null);
                setSelectedProfileId('');
                setIsLoading(false);
                return;
            }

            if (result.user?.role === UserRole.SUPER_ADMIN) {
                navigate('/super-admin');
            } else if (result.user?.role === UserRole.GUEST && result.user?.profileId && isGuestProfileIncomplete(result.user)) {
                const params = new URLSearchParams({
                    hotelId: selectedHotel,
                    reservationId,
                    profileId: String(result.user.profileId),
                });
                if (surname) params.append('surname', surname);
                params.append('verify', '1');
                if (searchParams.get('redirectToChat') === 'true') {
                    params.append('redirectToChat', 'true');
                }
                navigate(`/complete-guest-info?${params.toString()}`);
            } else if (searchParams.get('redirectToChat') === 'true') {
                try {
                    const chatsResponse = await api.get('/chats');
                    const chats = chatsResponse.data;
                    if (chats && chats.length > 0) {
                        navigate(`/dashboard/chat/${chats[0].id}`);
                    } else {
                        navigate('/dashboard');
                    }
                } catch {
                    navigate('/dashboard');
                }
            } else {
                navigate('/dashboard');
            }
        } catch (error: any) {
            const rawMessage = error.response?.data?.message
                || error.response?.data?.error
                || error.message
                || t('login.loginFailed');
            const stayMatch = typeof rawMessage === 'string'
                ? rawMessage.match(/Login is only allowed between reservation arrival and departure dates \((.+) – (.+)\)/)
                : null;
            const errorMessage = stayMatch
                ? t('login.outsideStayDates', { arrival: stayMatch[1], departure: stayMatch[2] })
                : rawMessage;
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNotifyUnknownReservation = async () => {
        const isSuperadminLogin = reservationId === '1' && surname?.toUpperCase() === 'SUPERADMIN';
        if (isSuperadminLogin) {
            return;
        }
        setUnknownReservationFeedback(null);
        if (!selectedHotel?.trim()) {
            setUnknownReservationFeedback({ type: 'error', text: t('login.hotelRequiredError') });
            return;
        }
        setUnknownReservationSending(true);
        try {
            await api.post('/auth/notify-unknown-reservation', {
                hotelId: selectedHotel,
                surname: surname.trim() || undefined,
            });
            setUnknownReservationFeedback({ type: 'success', text: t('login.unknownReservationEmailSent') });
        } catch (err: any) {
            const msg =
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                t('login.unknownReservationEmailFailed');
            setUnknownReservationFeedback({ type: 'error', text: typeof msg === 'string' ? msg : t('login.unknownReservationEmailFailed') });
        } finally {
            setUnknownReservationSending(false);
        }
    };

    const getGuestProfileIdentifier = (guest: Guest): string | undefined => {
        const profileIdentifier =
            guest?.profileId ??
            guest?.userId ??
            guest?.id ??
            guest?.guestProfile?.profileId ??
            guest?.guestProfile?.profileID ??
            guest?.guestProfile?.guestProfileId ??
            guest?.guestProfile?.guestProfileID;
        return profileIdentifier ? profileIdentifier.toString() : undefined;
    };

    const handleGuestSelection = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!selectedProfileId) {
            setError(t('login.pleaseSelectGuest'));
            return;
        }

        const idx = guests.findIndex(g => getGuestProfileIdentifier(g) === selectedProfileId);
        if (idx < 0 || idx >= guests.length) {
            setError(t('login.pleaseSelectGuest'));
            return;
        }

        const derivedProfileId = selectedProfileId;

        if (!derivedProfileId) {
            setError(t('login.guestSelectionFailed'));
            return;
        }

        setIsLoading(true);

        try {
            const result = await selectGuest(selectedHotel, reservationId, derivedProfileId, surname);

            const redirectToChat = searchParams.get('redirectToChat') === 'true';

            if (result.user?.role === UserRole.SUPER_ADMIN) {
                navigate('/super-admin');
            } else if (result.user?.role === UserRole.GUEST && derivedProfileId && isGuestProfileIncomplete(result.user)) {
                const params = new URLSearchParams({
                    hotelId: selectedHotel,
                    reservationId,
                    profileId: derivedProfileId,
                });
                if (surname) params.append('surname', surname);
                params.append('verify', '1');
                if (redirectToChat) {
                    params.append('redirectToChat', 'true');
                }
                navigate(`/complete-guest-info?${params.toString()}`);
            } else if (redirectToChat) {
                try {
                    const chatsResponse = await api.get('/chats');
                    const chats = chatsResponse.data;
                    if (chats && chats.length > 0) {
                        navigate(`/dashboard/chat/${chats[0].id}`);
                    } else {
                        navigate('/dashboard');
                    }
                } catch {
                    navigate('/dashboard');
                }
            } else {
                navigate('/dashboard');
            }
        } catch (error: any) {
            setError(error.response?.data?.message || t('login.guestSelectionFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddGuest = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!addGuestForm.firstName || !addGuestForm.lastName) {
            setError(t('login.firstNameLastNameRequired'));
            return;
        }

        setIsLoading(true);

        try {
            const response = await api.post('/auth/add-guest', {
                hotelId: selectedHotel,
                reservationId: reservationId,
                ...addGuestForm,
            });

            const newGuest: Guest = {
                profileId: response.data.profileId,
                firstName: addGuestForm.firstName,
                lastName: addGuestForm.lastName,
                guestProfile: response.data.guestProfile,
            };

            setGuests([...guests, newGuest]);
            setShowAddGuestDialog(false);
            setAddGuestForm({
                firstName: '',
                lastName: '',
                email: '',
                phoneNumber: '',
                birthdate: '',
                nationalId: '',
                nationality: '',
                country: '',
                city: '',
                postCode: '',
                sex: '',
                kvkk: false,
                passportId: '',
                productInterests: [],
                allergies: [],
            });
        } catch (error: any) {
            setError(error.response?.data?.message || t('login.failedToAddGuest'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleLanguageChange = (event: any) => {
        i18n.changeLanguage(event.target.value);
    };

    // Re-validate error messages when language changes
    React.useEffect(() => {
        if (surnameError) validateSurname(surname);
        if (reservationIdError) validateReservationId(reservationId);
        if (hotelError) validateHotel(selectedHotel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    const getLanguageCode = (lang: string) => {
        return lang.split('-')[0].toLowerCase();
    };

    // Common TextField styling — matches akis HTML .field
    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '14px',
            backgroundColor: isDark ? theme.palette.background.paper : '#fff',
            color: primaryTextColor,
            fontSize: '15px',
            fontWeight: 500,
            transition: 'all 0.2s ease',
            '& fieldset': {
                borderColor: isDark ? theme.palette.divider : '#D8DFE9',
                borderWidth: 1.5,
            },
            '&:hover': {
                '& fieldset': {
                    borderColor: accentColor,
                },
            },
            '&.Mui-focused': {
                '& fieldset': {
                    borderColor: accentColor,
                    borderWidth: 1.5,
                },
            },
        },
        '& .MuiInputLabel-root': {
            fontWeight: 600,
            color: secondaryTextColor,
            '&.Mui-focused': {
                color: accentColor,
            },
        },
        '& .MuiOutlinedInput-input::placeholder': {
            color: '#8A94A8',
            opacity: 1,
            fontWeight: 500,
        },
    };

    const primaryBtnSx = {
        mt: 2.5,
        mb: 0,
        py: '16px',
        borderRadius: '14px',
        fontSize: '15.5px',
        fontWeight: 700,
        textTransform: 'none' as const,
        backgroundColor: accentSoft,
        boxShadow: '0 6px 18px rgba(24,155,240,.28)',
        '&:hover': {
            backgroundColor: accentHover,
            boxShadow: '0 6px 18px rgba(24,155,240,.35)',
        },
        '&:active': {
            transform: 'scale(0.985)',
        },
        '&:disabled': {
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.12)' : '#cbd5e1',
            color: isDark ? 'rgba(255, 255, 255, 0.6)' : '#94a3b8',
            boxShadow: 'none',
        },
    };

    const outlineBtnSx = {
        py: '15px',
        borderRadius: '14px',
        border: `1.5px solid ${accentSoft}`,
        backgroundColor: isDark ? 'rgba(24,155,240,0.08)' : '#F6FBFF',
        color: accentColor,
        textTransform: 'none' as const,
        fontSize: '15px',
        fontWeight: 700,
        '&:hover': {
            border: `1.5px solid ${accentColor}`,
            backgroundColor: isDark ? 'rgba(24,155,240,0.15)' : '#EAF6FE',
        },
    };

    return (
        <Box
            sx={{
                minHeight: '100dvh',
                height: '100dvh',
                bgcolor: pageBg,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    maxWidth: 520,
                    mx: 'auto',
                    px: '14px',
                    pt: 'calc(env(safe-area-inset-top, 0px) + 20px)',
                    pb: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        p: '26px 18px 24px',
                        borderRadius: '22px',
                        background: surfaceBg,
                        border: surfaceBorder,
                        color: primaryTextColor,
                        position: 'relative',
                        boxShadow: isDark
                            ? '0 12px 40px rgba(0, 0, 0, 0.4)'
                            : '0 12px 40px rgba(21,41,77,.10)',
                        my: 'auto',
                        width: '100%',
                    }}
                >
                    {/* Logo + theme + language — auth-head */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1.25,
                            flexWrap: 'wrap',
                        }}
                    >
                        <HeyHotelLogo />
                        <Tooltip title={isDark ? t('sidebar.light') : t('sidebar.dark')}>
                            <IconButton onClick={handleThemeToggle} sx={chipBtnSx} aria-label="Tema">
                                {isDark ? <LightMode sx={{ fontSize: 20 }} /> : <DarkMode sx={{ fontSize: 20 }} />}
                            </IconButton>
                        </Tooltip>
                        <FormControl size="small" sx={{ minWidth: 46 }}>
                            <Select
                                value={i18n.language}
                                onChange={handleLanguageChange}
                                IconComponent={() => null}
                                renderValue={(value) => {
                                    const langCode = getLanguageCode(value);
                                    const countryCode = languageFlags[langCode] || languageFlags[value] || 'gb';
                                    return (
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <FlagIcon countryCode={countryCode} size={20} />
                                        </Box>
                                    );
                                }}
                                sx={{
                                    width: 46,
                                    height: 46,
                                    borderRadius: '14px',
                                    bgcolor: isDark ? theme.palette.action.hover : '#fff',
                                    border: `1px solid ${mutedBorderColor}`,
                                    boxShadow: isDark ? 'none' : '0 1px 4px rgba(21,41,77,.06)',
                                    '& .MuiSelect-select': {
                                        p: '0 !important',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: '46px !important',
                                    },
                                    '& fieldset': { border: 'none' },
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            borderRadius: '14px',
                                            mt: 1,
                                            boxShadow: '0 8px 24px rgba(21,41,77,.12)',
                                        },
                                    },
                                }}
                            >
                                {Object.entries(languageFlags).map(([lang, flag]) => (
                                    <MenuItem key={lang} value={lang}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.5 }}>
                                            <FlagIcon countryCode={flag} size={28} />
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Typography
                        component="h1"
                        sx={{
                            textAlign: 'center',
                            color: accentColor,
                            fontWeight: 800,
                            fontSize: '25px',
                            mt: '18px',
                            letterSpacing: '-0.3px',
                        }}
                    >
                        {t('login.title')}
                    </Typography>
                    <Typography
                        sx={{
                            textAlign: 'center',
                            color: secondaryTextColor,
                            fontSize: '14px',
                            mt: 1,
                            lineHeight: 1.5,
                        }}
                    >
                        {t('login.subtitle')}
                    </Typography>

                    {error && (
                        <Alert
                            severity="error"
                            sx={{
                                mt: 2,
                                mb: 1,
                                borderRadius: '12px',
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    {unknownReservationFeedback && (
                        <Alert
                            severity={unknownReservationFeedback.type === 'success' ? 'success' : 'error'}
                            sx={{ mt: 2, mb: 1, borderRadius: '12px' }}
                            onClose={() => setUnknownReservationFeedback(null)}
                        >
                            {unknownReservationFeedback.text}
                        </Alert>
                    )}

                    <Box>
                        {!showGuestSelection ? (
                            <Box
                                component="form"
                                onSubmit={handleLogin}
                                sx={{ mt: 0.5 }}
                            >
                                {/* Hotel Selection */}
                                <FormControl
                                    fullWidth
                                    margin="normal"
                                    required={reservationId !== '1' && surname?.toUpperCase() !== 'SUPERADMIN'}
                                    error={!!hotelError}
                                    sx={{
                                        mt: '26px',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '14px',
                                            backgroundColor: isDark ? theme.palette.background.paper : '#fff',
                                            color: primaryTextColor,
                                            '& fieldset': {
                                                borderColor: isDark ? theme.palette.divider : '#D8DFE9',
                                                borderWidth: 1.5,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: accentColor,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: accentColor,
                                                borderWidth: 1.5,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            color: '#6B7488',
                                            '&.Mui-focused': {
                                                color: accentColor,
                                            },
                                        },
                                    }}
                                >
                                    <InputLabel id="hotel-select-label" shrink>
                                        {reservationId !== '1' && surname?.toUpperCase() !== 'SUPERADMIN' ? t('login.hotelRequired') : t('login.hotel')}
                                    </InputLabel>
                                    <Select
                                        labelId="hotel-select-label"
                                        id="hotel-select"
                                        value={selectedHotel}
                                        label={reservationId !== '1' && surname?.toUpperCase() !== 'SUPERADMIN' ? t('login.hotelRequired') : t('login.hotel')}
                                        onChange={(e) => {
                                            setSelectedHotel(e.target.value);
                                            if (hotelError) validateHotel(e.target.value);
                                        }}
                                        onBlur={() => validateHotel(selectedHotel)}
                                        disabled={hotelsLoading || isHotelLockedFromUrl}
                                        IconComponent={KeyboardArrowDownIcon}
                                        notched
                                        renderValue={(value) => {
                                            if (!value) {
                                                return (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                                        <BusinessIcon sx={{ color: accentColor, fontSize: 20 }} />
                                                        <Typography sx={{ color: '#7C879B', fontWeight: 600, fontSize: '15.5px' }}>
                                                            {t('login.none')}
                                                        </Typography>
                                                    </Box>
                                                );
                                            }
                                            const hotel = hotels.find(h => h.id === value);
                                            if (!hotel) {
                                                return (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                                        <BusinessIcon sx={{ color: accentColor, fontSize: 20 }} />
                                                        <Typography sx={{ color: '#7C879B', fontWeight: 600, fontSize: '15.5px' }}>
                                                            {t('login.none')}
                                                        </Typography>
                                                    </Box>
                                                );
                                            }
                                            return (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                                    <HotelIcon sx={{ color: accentColor, fontSize: 20 }} />
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography sx={{ fontWeight: 600, color: primaryTextColor, fontSize: '15.5px', lineHeight: 1.2 }}>
                                                            {hotel.name}
                                                        </Typography>
                                                        <Typography sx={{ color: '#A9B2C3', fontSize: '11.5px', letterSpacing: '0.04em', mt: '1px' }}>
                                                            {hotel.code}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            );
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    maxHeight: 400,
                                                    borderRadius: '14px',
                                                    mt: 1,
                                                    boxShadow: '0 12px 32px rgba(21,41,77,.12)',
                                                    border: `1px solid ${mutedBorderColor}`,
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem value="">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                                <BusinessIcon sx={{ color: '#9ca3af', fontSize: '1.25rem' }} />
                                                <Typography sx={{ color: secondaryTextColor, fontStyle: 'italic' }}>
                                                    {t('login.none')}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                        {hotels.map((hotel) => (
                                            <MenuItem key={hotel.id} value={hotel.id}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: '12px',
                                                            backgroundColor: isDark ? 'rgba(24, 155, 240, 0.15)' : '#EAF6FE',
                                                            color: accentColor,
                                                        }}
                                                    >
                                                        <HotelIcon sx={{ fontSize: '1.35rem' }} />
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: primaryTextColor,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {hotel.name}
                                                        </Typography>
                                                        <Typography sx={{ color: secondaryTextColor, fontSize: '0.75rem', display: 'block', mt: 0.25 }}>
                                                            {hotel.code}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {hotelError && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75, display: 'block', fontWeight: 500 }}>
                                            {hotelError}
                                        </Typography>
                                    )}
                                </FormControl>

                                {/* Guest Login Fields */}
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="surname"
                                    placeholder={`${t('login.surname')} *`}
                                    name="surname"
                                    autoComplete="family-name"
                                    value={surname}
                                    onChange={(e) => {
                                        setSurname(e.target.value);
                                        if (surnameError) validateSurname(e.target.value);
                                    }}
                                    onBlur={(e) => validateSurname(e.target.value)}
                                    error={!!surnameError}
                                    helperText={surnameError}
                                    InputLabelProps={{ shrink: false }}
                                    sx={{ ...textFieldSx, mt: 2.5, '& .MuiInputLabel-root': { display: 'none' } }}
                                />
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="reservationId"
                                    placeholder={`${t('login.reservationId')} *`}
                                    name="reservationId"
                                    value={reservationId}
                                    onChange={(e) => {
                                        setReservationId(e.target.value);
                                        if (reservationIdError) validateReservationId(e.target.value);
                                    }}
                                    onBlur={(e) => validateReservationId(e.target.value)}
                                    error={!!reservationIdError}
                                    helperText={reservationIdError}
                                    inputProps={{ inputMode: 'numeric' }}
                                    sx={{ ...textFieldSx, mt: 2.5, '& .MuiInputLabel-root': { display: 'none' } }}
                                />

                                {!(reservationId === '1' && surname?.toUpperCase() === 'SUPERADMIN') && (
                                    <Button
                                        type="button"
                                        fullWidth
                                        variant="text"
                                        size="medium"
                                        disabled={unknownReservationSending || hotelsLoading || isLoading}
                                        onClick={handleNotifyUnknownReservation}
                                        sx={{
                                            mt: 2.5,
                                            mb: 0,
                                            color: accentColor,
                                            fontWeight: 700,
                                            fontSize: '13.5px',
                                            textTransform: 'none',
                                            '&:hover': {
                                                backgroundColor: isDark ? 'rgba(24, 155, 240, 0.08)' : 'rgba(24, 155, 240, 0.06)',
                                            },
                                        }}
                                    >
                                        {unknownReservationSending ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CircularProgress size={18} sx={{ color: accentColor }} />
                                                {t('login.unknownReservationSending')}
                                            </Box>
                                        ) : (
                                            t('login.dontKnowReservationNumber')
                                        )}
                                    </Button>
                                )}

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    disabled={isLoading}
                                    sx={primaryBtnSx}
                                >
                                    {isLoading ? <CircularProgress size={24} color="inherit" /> : t('login.signIn')}
                                </Button>
                            </Box>
                        ) : (
                            <Box
                                component="form"
                                onSubmit={handleGuestSelection}
                                sx={{ mt: 1 }}
                            >
                                <Typography sx={{ fontWeight: 800, color: primaryTextColor, mb: 2, fontSize: '16px' }}>
                                    {t('login.multipleGuests')}
                                </Typography>

                                {reservation && (
                                    <Box
                                        sx={{
                                            mb: 3,
                                            p: '13px 14px',
                                            borderRadius: '12px',
                                            backgroundColor: isDark ? 'rgba(24, 155, 240, 0.1)' : '#E8F4FD',
                                            border: 'none',
                                        }}
                                    >
                                        <Typography sx={{ color: mutedTextColor, fontWeight: 500, fontSize: '13px' }}>
                                            {t('login.reservation')}: <strong>{reservation.confirmationNumber || reservationId}</strong>
                                            {reservation.roomNumber && ` | ${t('login.room')}: ${reservation.roomNumber}`}
                                        </Typography>
                                    </Box>
                                )}

                                <FormControl component="fieldset" fullWidth sx={{ mt: 1 }}>
                                    <FormLabel component="legend" sx={{ fontWeight: 700, color: mutedTextColor, mb: 1.5, fontSize: '14px' }}>
                                        {t('login.selectGuest')}
                                    </FormLabel>
                                    <RadioGroup
                                        value={selectedGuestIndex !== null ? selectedGuestIndex.toString() : ''}
                                        onChange={(e) => {
                                            const index = Number(e.target.value);
                                            if (!Number.isInteger(index) || index < 0 || index >= guests.length) {
                                                setSelectedGuestIndex(null);
                                                setSelectedProfileId('');
                                                setError(t('login.pleaseSelectGuest'));
                                                return;
                                            }
                                            const selectedGuest = guests[index];
                                            const profileIdentifier = getGuestProfileIdentifier(selectedGuest);
                                            if (!profileIdentifier) {
                                                setSelectedGuestIndex(null);
                                                setSelectedProfileId('');
                                                setError(t('login.guestSelectionFailed'));
                                                return;
                                            }
                                            setSelectedGuestIndex(index);
                                            setSelectedProfileId(profileIdentifier);
                                            setError('');
                                        }}
                                    >
                                        {guests.map((guest, index) => {
                                            const profileIdentifier = getGuestProfileIdentifier(guest);
                                            const isSelected = selectedGuestIndex === index;
                                            const isSelectable = Boolean(profileIdentifier);
                                            return (
                                                <Card
                                                    key={index}
                                                    elevation={0}
                                                    sx={{
                                                        mb: 1.25,
                                                        borderRadius: '14px',
                                                        border: isSelected ? `1.5px solid ${accentColor}` : `1px solid ${mutedBorderColor}`,
                                                        backgroundColor: isSelected ? selectedCardBg : cardBg,
                                                        transition: 'all 0.2s ease',
                                                        cursor: 'pointer',
                                                        opacity: isSelectable ? 1 : 0.65,
                                                        boxShadow: isDark ? 'none' : '0 1px 3px rgba(21,41,77,.05)',
                                                        '&:hover': {
                                                            borderColor: accentColor,
                                                            backgroundColor: selectedCardBg,
                                                        },
                                                    }}
                                                    onClick={() => {
                                                        if (isSelectable && profileIdentifier) {
                                                            setSelectedGuestIndex(index);
                                                            setSelectedProfileId(profileIdentifier);
                                                            setError('');
                                                        }
                                                    }}
                                                >
                                                    <CardContent sx={{ py: 1.75, px: 2, '&:last-child': { pb: 1.75 } }}>
                                                        <FormControlLabel
                                                            value={index.toString()}
                                                            control={<Radio sx={{ color: accentColor, '&.Mui-checked': { color: accentColor } }} />}
                                                            disabled={!isSelectable}
                                                            label={
                                                                <Typography sx={{ fontWeight: isSelected ? 700 : 600, color: isSelected ? accentColor : mutedTextColor, fontSize: '15px' }}>
                                                                    {`${guest.firstName} ${guest.lastName}`}
                                                                </Typography>
                                                            }
                                                            sx={{ m: 0, width: '100%' }}
                                                        />
                                                    </CardContent>
                                                </Card>
                                            );
                                        })}
                                    </RadioGroup>
                                </FormControl>

                                <Button
                                    fullWidth
                                    variant="outlined"
                                    sx={{ ...outlineBtnSx, mt: 2, mb: 1 }}
                                    onClick={() => {
                                        if (selectedHotel && reservationId) {
                                            const params = new URLSearchParams({ hotelId: selectedHotel, reservationId: reservationId });
                                            if (surname) params.append('surname', surname);
                                            navigate(`/add-guest?${params.toString()}`);
                                        }
                                    }}
                                    disabled={isLoading || !selectedHotel || !reservationId}
                                >
                                    {t('login.addGuestToRoom')}
                                </Button>

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    disabled={isLoading || selectedGuestIndex === null}
                                    sx={primaryBtnSx}
                                >
                                    {isLoading ? <CircularProgress size={24} color="inherit" /> : t('login.continue')}
                                </Button>

                                <Button
                                    fullWidth
                                    variant="text"
                                    sx={{
                                        mt: 1.5,
                                        py: 1.25,
                                        color: accentColor,
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        fontSize: '14.5px',
                                    }}
                                    onClick={() => {
                                        setShowGuestSelection(false);
                                        setGuests([]);
                                        setSelectedProfileId('');
                                        setSelectedGuestIndex(null);
                                    }}
                                    disabled={isLoading}
                                >
                                    {t('login.back')}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Box>

            {/* Add Guest Dialog */}
            <Dialog
                open={showAddGuestDialog}
                onClose={() => setShowAddGuestDialog(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 20px 60px rgba(9, 158, 217, 0.3)' } }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: primaryTextColor, borderBottom: '1px solid #e5e7eb', pb: 2 }}>
                    {t('login.newGuest')}
                </DialogTitle>
                <Box component="form" onSubmit={handleAddGuest}>
                    <DialogContent>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField required fullWidth label={t('login.firstName')} value={addGuestForm.firstName} onChange={(e) => setAddGuestForm({ ...addGuestForm, firstName: e.target.value })} sx={textFieldSx} />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField required fullWidth label={t('login.lastName')} value={addGuestForm.lastName} onChange={(e) => setAddGuestForm({ ...addGuestForm, lastName: e.target.value })} sx={textFieldSx} />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField fullWidth type="email" label={t('login.email')} value={addGuestForm.email} onChange={(e) => setAddGuestForm({ ...addGuestForm, email: e.target.value })} sx={textFieldSx} />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField fullWidth label={t('login.phoneNumber')} value={addGuestForm.phoneNumber} onChange={(e) => setAddGuestForm({ ...addGuestForm, phoneNumber: e.target.value })} sx={textFieldSx} />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <FormControlLabel
                                    control={<Checkbox checked={addGuestForm.kvkk} onChange={(e) => setAddGuestForm({ ...addGuestForm, kvkk: e.target.checked })} />}
                                    label={t('login.kvkk')}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb', gap: 1 }}>
                        <Button onClick={() => setShowAddGuestDialog(false)} disabled={isLoading} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3 }}>
                            {t('login.cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isLoading}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 2,
                                px: 3,
                                background: 'linear-gradient(135deg, #099ed9 0%, #087bb8 100%)',
                                boxShadow: '0 4px 15px rgba(9, 158, 217, 0.4)',
                                '&:hover': { background: 'linear-gradient(135deg, #087bb8 0%, #065a87 100%)' },
                                '&:disabled': { background: '#cbd5e1', boxShadow: 'none' },
                            }}
                        >
                            {isLoading ? <CircularProgress size={24} color="inherit" /> : t('login.addGuest')}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </Box>
    );
};

export default GuestLoginPage;
